/**
 * Quest instructions & resources — pure core.
 *
 * Mirrors the rules of the quest_instructions_validate trigger
 * (20260925130000) so the editor fails early with the same verdict the
 * database would reach. The trigger stays authoritative; this is only the
 * early warning.
 */

export interface QuestResource { label: string; url: string }

export interface QuestInstructions {
  questId: string;
  instructions: string;
  resources: QuestResource[];
  updatedAt: string | null;
}

export interface QuestInstructionsDraft { instructions: string; resources: QuestResource[] }

/** An AI draft for the creator to review. `droppedLinks` counts links the
 *  model returned that were not in the quest's own text, and were removed. */
export interface QuestInstructionsAiDraft extends QuestInstructionsDraft { droppedLinks: number }

export const INSTRUCTIONS_MAX_CHARS = 8000;
export const RESOURCES_MAX = 20;
export const RESOURCE_LABEL_MAX = 120;
export const RESOURCE_URL_MAX = 2048;

export type ResourceError = "labelRequired" | "labelTooLong" | "urlInvalid";

const SAFE_URL = /^https?:\/\/\S+$/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** A bare `example.com/x` gets https://. Anything with another scheme is left
 *  as-is so that it stays invalid instead of turning into https://javascript:… */
export function normalizeUrl(raw: string): string {
  const v = raw.trim();
  if (!v) return "";
  if (HAS_SCHEME.test(v)) return v;
  return `https://${v}`;
}

export function isSafeUrl(url: string): boolean {
  if (!url || url.length > RESOURCE_URL_MAX || !SAFE_URL.test(url)) return false;
  try {
    const u = new URL(url);
    return (u.protocol === "https:" || u.protocol === "http:") && u.hostname.length > 0;
  } catch {
    return false;
  }
}

export function validateResource(r: QuestResource): { label?: ResourceError; url?: ResourceError } | null {
  const errors: { label?: ResourceError; url?: ResourceError } = {};
  const label = r.label.trim();
  if (!label) errors.label = "labelRequired";
  else if (label.length > RESOURCE_LABEL_MAX) errors.label = "labelTooLong";
  if (!isSafeUrl(r.url.trim())) errors.url = "urlInvalid";
  return errors.label || errors.url ? errors : null;
}

/** First blocking problem as an i18n key, or null. */
export function validateDraft(d: QuestInstructionsDraft): string | null {
  if (d.instructions.length > INSTRUCTIONS_MAX_CHARS) return "questInstructions.errors.instructionsTooLong";
  if (d.resources.length > RESOURCES_MAX) return "questInstructions.errors.tooManyLinks";
  if (d.resources.some((r) => validateResource(r) !== null)) return "questInstructions.errors.fixLinks";
  return null;
}

/** The shape both adapters persist: instructions and each label/url trimmed.
 *  One definition keeps the mock and the real adapter in lockstep. */
export function normalizeDraft(d: QuestInstructionsDraft): QuestInstructionsDraft {
  return {
    instructions: d.instructions.trim(),
    resources: d.resources.map((r) => ({ label: r.label.trim(), url: r.url.trim() })),
  };
}

/**
 * Where a gated read stands, for every surface that shows or edits it.
 * A failed background refetch keeps the last good row in `data`, so it is
 * still "settled"; only a read that never produced a row is "failed".
 */
export type InstructionsReadState = "pending" | "failed" | "settled";

export function instructionsReadState(r: {
  data: QuestInstructions | null | undefined;
  isPending: boolean;
  isError: boolean;
}): InstructionsReadState {
  if (r.data !== undefined) return "settled";
  if (r.isPending) return "pending";
  return r.isError ? "failed" : "settled";
}

export function isEmptyInstructions(
  d: Pick<QuestInstructions, "instructions" | "resources"> | null | undefined,
): boolean {
  return !d || (d.instructions.trim() === "" && d.resources.length === 0);
}

function canonical(d: QuestInstructionsDraft): string {
  return JSON.stringify({
    i: d.instructions.trim(),
    r: d.resources.map((r) => [r.label.trim(), r.url.trim()]),
  });
}

export function sameInstructions(a: QuestInstructionsDraft, b: QuestInstructionsDraft): boolean {
  return canonical(a) === canonical(b);
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function formatInstructionsForChat(args: {
  heading: string;
  linksHeading: string;
  draft: QuestInstructionsDraft;
}): string {
  const parts = [args.heading];
  const body = args.draft.instructions.trim();
  if (body) parts.push(body);
  if (args.draft.resources.length > 0) {
    parts.push([args.linksHeading, ...args.draft.resources.map((r) => `• ${r.label}: ${r.url}`)].join("\n"));
  }
  return parts.join("\n\n");
}
